import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VoicePreview from "@/components/VoicePreview";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Clock, BookOpen, Package, Check } from "lucide-react";

const CATEGORIES = ["All", "Flagship", "Beginner", "Revenue", "Marketing", "Operations", "Automation"];

function firstAudioUrl(course) {
  for (const m of course.modules || []) {
    for (const l of m.lessons || []) {
      if (l.audio_url) return l.audio_url;
    }
  }
  return null;
}

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [toolkits, setToolkits] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    const p = cat === "All" ? api.get("/courses") : api.get(`/courses?category=${cat}`);
    p.then((r) => setCourses(r.data)).finally(() => setLoading(false));
  }, [cat]);

  useEffect(() => {
    api.get("/products?tier=digital")
      .then((r) => setToolkits((r.data || []).filter((p) => (p.price || 0) > 0)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setOwned(new Set()); return; }
    api.get("/me/toolbox")
      .then((r) => setOwned(new Set((r.data?.items || []).map((p) => p.id))))
      .catch(() => {});
  }, [user]);

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-4">The Course Library</div>
        <h1 className="font-display text-5xl md:text-6xl max-w-3xl leading-tight">Master every layer of the <span className="italic text-brand-pink">Airbnb business</span>.</h1>
        <p className="mt-6 text-neutral-600 max-w-2xl text-lg">From your first listing to your first exit — a course library that grows with your business.</p>

        <div className="mt-12 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`courses-filter-${c.toLowerCase()}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition ${
                cat === c ? "bg-brand-pink text-white btn-pink-glow" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
          {loading && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-3xl bg-neutral-100 aspect-[4/5] animate-pulse" />
          ))}
          {!loading && courses.map((c) => (
            <Link
              to={`/courses/${c.id}`}
              key={c.id}
              data-testid={`course-card-${c.id}`}
              className="group rounded-3xl overflow-hidden bg-neutral-50 hover:-translate-y-1 transition-transform duration-300 flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {c.featured && (
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-brand-pink text-white text-[10px] uppercase tracking-widest">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-pink mb-3">
                  <Heart className="w-3 h-3" fill="#E91E63" /> {c.category}
                </div>
                <h3 className="font-display text-2xl mb-2">{c.title}</h3>
                <p className="text-sm text-neutral-600 mb-4 line-clamp-2">{c.tagline}</p>

                {c.modules?.length > 0 && (
                  <div className="mb-5 rounded-2xl bg-white border border-neutral-100 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Inside this course</div>
                    <ul className="space-y-1.5">
                      {c.modules.slice(0, 3).map((m, i) => (
                        <li key={m.id} className="text-xs text-neutral-700 flex items-start gap-2">
                          <span className="font-display text-[11px] text-brand-pink leading-5 w-4 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                          <span className="truncate">{m.title}</span>
                        </li>
                      ))}
                      {c.modules.length > 3 && (
                        <li className="text-[11px] text-neutral-400 pl-6">+ {c.modules.length - 3} more modules</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="mt-auto flex items-center justify-between text-sm">
                  <div className="flex gap-4 text-neutral-500">
                    <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {c.chapters_count}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration_hrs}h</span>
                  </div>
                  <span className="font-display text-lg text-neutral-900">{c.price === 0 ? "Free" : `$${c.price}`}</span>
                </div>
                {firstAudioUrl(c) && (
                  <div className="mt-4">
                    <VoicePreview
                      audioUrl={firstAudioUrl(c)}
                      previewSeconds={30}
                      label="Hear Kristy narrate this"
                      testId={`course-voice-preview-${c.id}`}
                    />
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        {toolkits.length > 0 && (
          <>
            <div className="mt-24 mb-10">
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Premium Toolkits</div>
              <h2 className="font-display text-4xl md:text-5xl max-w-2xl leading-tight">
                Plug-and-play <span className="italic text-brand-pink">toolkits</span>.
              </h2>
              <p className="mt-5 text-neutral-600 max-w-2xl">
                Standalone bundles — pricing playbooks, SOP packs, cold-call scripts, VA training docs. Buy once, download forever, lives in your Toolbox.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {toolkits.map((p) => {
                const isOwned = owned.has(p.id);
                return (
                  <Link
                    key={p.id}
                    to={`/toolkits/${p.id}`}
                    data-testid={`toolkit-card-${p.id}`}
                    className="rounded-3xl bg-neutral-50 border border-neutral-100 p-6 flex flex-col group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-pink mb-3">
                      <Package className="w-3 h-3" /> {p.category || "Toolkit"}
                    </div>
                    <h3 className="font-display text-2xl mb-2 leading-tight group-hover:text-brand-pink transition-colors">{p.name}</h3>
                    <p className="text-sm text-neutral-600 mb-6 line-clamp-3">{p.description}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <span className="font-display text-2xl">${p.price}</span>
                      {isOwned ? (
                        <span data-testid={`toolkit-owned-${p.id}`} className="inline-flex items-center h-9 px-4 rounded-full bg-brand-pink/10 text-brand-pink text-xs font-medium">
                          <Check className="w-3.5 h-3.5 mr-1.5" /> In your toolbox
                        </span>
                      ) : (
                        <span data-testid={`toolkit-preview-${p.id}`} className="inline-flex items-center h-9 px-4 rounded-full bg-brand-pink text-white text-xs btn-pink-glow">
                          See what's inside
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
